///
/// EarthBrowser
///
/// Created by Matt Giger on 1/3/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Database } from "@/earthbrowser/database/Database"
import type { BuoySource } from "@shared-types/buoy"

export interface BuoyStationProperties {
	station_id: string
	name: string
	lat: number
	lon: number
	type: string | null
	owner: string | null
	is_drifting: boolean
	pgm: string | null
	met: boolean
	currents: boolean
	water_quality: boolean
	dart: boolean
	buoycam: boolean
	source: BuoySource
	wmo_id: string | null
	coops_id: string | null
}

/** Row shape as SQLite returns it (booleans as 0/1) */
type StationRow = {
	station_id: string
	name: string
	lat: number
	lon: number
	type: string | null
	owner: string | null
	is_drifting: number
	pgm: string | null
	met: number
	currents: number
	water_quality: number
	dart: number
	buoycam: number
	source: string | null
	wmo_id: string | null
	coops_id: string | null
}

const cBatchSize = 100

const cColumns = `station_id, name, lat, lon, type, owner, is_drifting, pgm, met, currents, water_quality, dart, buoycam, source, wmo_id, coops_id`

function fromRow(r: StationRow): BuoyStationProperties {
	return {
		station_id: r.station_id,
		name: r.name,
		lat: r.lat,
		lon: r.lon,
		type: r.type,
		owner: r.owner,
		is_drifting: r.is_drifting === 1,
		pgm: r.pgm,
		met: r.met === 1,
		currents: r.currents === 1,
		water_quality: r.water_quality === 1,
		dart: r.dart === 1,
		buoycam: r.buoycam === 1,
		source: (r.source ?? "ndbc") as BuoySource,
		wmo_id: r.wmo_id,
		coops_id: r.coops_id,
	}
}

export class BuoyStationsTable {
	public static async create(db: Database): Promise<void> {
		await db.execMany([
			{
				sql: `create table if not exists buoy_stations (
					station_id text primary key,
					name text not null,
					lat real not null,
					lon real not null,
					type text,
					owner text,
					is_drifting integer not null default 0,
					pgm text,
					met integer not null default 0,
					currents integer not null default 0,
					water_quality integer not null default 0,
					dart integer not null default 0,
					buoycam integer not null default 0,
					source text not null default 'ndbc',
					wmo_id text,
					coops_id text
				)`
			},
			{ sql: "create index if not exists idx_buoy_stations_type on buoy_stations(type)" },
			{ sql: "create index if not exists idx_buoy_stations_dart on buoy_stations(dart)" },
		])
	}

	public static async replaceAll(rows: BuoyStationProperties[]): Promise<void> {
		await Database.db.exec("delete from buoy_stations", [], "critical")
		await this.upsert(rows)
	}

	public static async upsert(rows: BuoyStationProperties[]): Promise<void> {
		const statement = `insert into buoy_stations (${cColumns})
			values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
			on conflict(station_id) do update set
				name = excluded.name,
				lat = excluded.lat,
				lon = excluded.lon,
				type = excluded.type,
				owner = excluded.owner,
				is_drifting = excluded.is_drifting,
				pgm = excluded.pgm,
				met = excluded.met,
				currents = excluded.currents,
				water_quality = excluded.water_quality,
				dart = excluded.dart,
				buoycam = excluded.buoycam,
				source = excluded.source,
				wmo_id = excluded.wmo_id,
				coops_id = excluded.coops_id`

		for (let i = 0; i < rows.length; i += cBatchSize) {
			const batch = rows.slice(i, i + cBatchSize)
			await Database.db.bulkInsert(statement, batch.map(s => [
				s.station_id,
				s.name,
				s.lat,
				s.lon,
				s.type,
				s.owner,
				s.is_drifting ? 1 : 0,
				s.pgm,
				s.met ? 1 : 0,
				s.currents ? 1 : 0,
				s.water_quality ? 1 : 0,
				s.dart ? 1 : 0,
				s.buoycam ? 1 : 0,
				s.source,
				s.wmo_id,
				s.coops_id,
			]))
		}
	}

	private static async select(where: string, params: unknown[] = []): Promise<BuoyStationProperties[]> {
		const rows = await Database.db.exec(
			`select ${cColumns} from buoy_stations ${where} order by station_id`,
			params,
			"low"
		) as StationRow[]
		return rows.map(fromRow)
	}

	public static async getAll(): Promise<BuoyStationProperties[]> {
		return this.select("")
	}

	/** Stations inside a lat/lon box (no antimeridian wrap), for the storm column pins */
	public static async getInBBox(minLon: number, minLat: number, maxLon: number, maxLat: number, limit: number = 40): Promise<BuoyStationProperties[]> {
		const rows = await Database.db.exec(
			`select ${cColumns} from buoy_stations
			where lat between ? and ? and lon between ? and ?
			order by station_id
			limit ${Math.max(1, Math.min(limit, 500))}`,
			[minLat, maxLat, minLon, maxLon],
			"high"
		) as StationRow[]
		return rows.map(fromRow)
	}

	public static async getById(stationId: string): Promise<BuoyStationProperties | null> {
		const rows = await this.select("where station_id = ?", [stationId])
		return rows[0] ?? null
	}

	public static async getDartBuoys(): Promise<BuoyStationProperties[]> {
		return this.select("where dart = 1")
	}

	public static async getByType(type: string): Promise<BuoyStationProperties[]> {
		return this.select("where type = ?", [type])
	}

	public static async getCount(): Promise<number> {
		const rows = await Database.db.exec(
			`select count(*) as count from buoy_stations`,
			[],
			"low"
		) as Array<{ count: number }>
		return rows[0]?.count ?? 0
	}

	public static async getTypes(): Promise<string[]> {
		const rows = await Database.db.exec(
			`select distinct type from buoy_stations where type is not null order by type`,
			[],
			"low"
		) as Array<{ type: string }>
		return rows.map(r => r.type)
	}
}
